import {
  noyauCodeSchema,
  noyauKeySchema,
  tagCategorySchema,
  wordTagSchema,
} from '@words_interface/shared';
import type { NoyauCode, NoyauKey, TagCategory, WordTag } from '@words_interface/shared';
import { z } from 'zod';

// The noyau + tag shapes are the cross-boundary source of truth in
// @words_interface/shared; re-exported here so existing `@/schemas/word` imports
// (across the app, adapters, and MSW) keep resolving unchanged.
export { noyauCodeSchema, noyauKeySchema, tagCategorySchema, wordTagSchema };
export type { NoyauCode, NoyauKey, TagCategory, WordTag };

/**
 * Per-noyau integer weight, ordered [SGP, SPG, PSG, PGS, GPS, GSP] (matches
 * {@link NOYAU_ORDER}). Maps 1:1 to the Tahiti `ponderation_<noyau>` columns. A
 * noyau's percentage is derived from its share of the sum (see `noyauPercentages`),
 * exactly as the 2026-06-16 schema CR specifies ("6 ints, UI shows %"). Replaces
 * the former per-source model — there is no longer any per-source name/link/date.
 */
export const noyauPonderationSchema = z.tuple([
  z.number(),
  z.number(),
  z.number(),
  z.number(),
  z.number(),
  z.number(),
]);
export type NoyauPonderation = z.infer<typeof noyauPonderationSchema>;

/** Single-letter algorithm-owned function code: G=Guerrier, P=Souverain, S=Producteur. */
export const fonctionCodeSchema = z.enum(['G', 'P', 'S']);
export type FonctionCode = z.infer<typeof fonctionCodeSchema>;

/**
 * "Étapes de raisonnement" (Point 4): 6 fixed steps, each just a noyau
 * ponderation (no other fields). There is no DB column yet — the tuple is
 * bridged to/from a hidden fenced block inside the Tahiti `content` markdown
 * (see lib/reasoning-steps.ts), fully independent of the word's own
 * `noyauPonderation`.
 */
export const reasoningStepsSchema = z.tuple([
  noyauPonderationSchema,
  noyauPonderationSchema,
  noyauPonderationSchema,
  noyauPonderationSchema,
  noyauPonderationSchema,
  noyauPonderationSchema,
]);
export type ReasoningSteps = z.infer<typeof reasoningStepsSchema>;

export const wordSchema = z.object({
  id: z.string().uuid(),
  /** Tahiti's numeric primary key (`pms_word.id`) — the identifier internal links target (`[text](#<numericId>)`), distinct from `id` (the guid, used for routing/writes) and `slug` (a client-only cosmetic value). */
  numericId: z.string(),
  slug: z.string(),
  /** Single title (the bold/light split was a frontend-only invention). */
  title: z.string(),
  /** Six integer weights; the UI renders each as a % of their sum. */
  noyauPonderation: noyauPonderationSchema,
  /**
   * The backend `noyau_sing` — the curated dominant noyau, used as the dominant
   * fallback when every ponderation weight is zero. Read-only display.
   */
  noyauSing: noyauCodeSchema.nullable(),
  /**
   * Whether the noyau has been curated/locked (Tahiti `noyau_validated`). When
   * true, `noyauSing` is the single noyau shown in view mode and ponderation
   * editing is disabled until un-validated.
   */
  noyauValidated: z.boolean(),
  tags: z.array(wordTagSchema),
  /** Body — raw markdown, bridged 1:1 to/from the Tahiti `content` column. */
  content: z.string(),
  /** See {@link reasoningStepsSchema}. Defaults to all-empty when the word has none yet. */
  reasoningSteps: reasoningStepsSchema,
  /** "À voir aussi" — freeform markdown (Tahiti `see_also`). */
  seeAlso: z.string(),
  // ── Read-only display (algorithm / Lexcore / Patrick owned) ──────────────────
  /** Determined function — G (Guerrier) / P (Souverain) / S (Producteur). */
  fonction: fonctionCodeSchema.nullable(),
  /** Grammatical gender code/label (M/F/N/A). */
  genre: z.string().nullable(),
  /** Whether the word is flagged for review (Tahiti `to_check`). */
  toCheck: z.boolean(),
  /** Why the word is flagged to-check (Tahiti `reasons`). */
  reasons: z.string().nullable(),
  /** Editorial revision date as ISO YYYY-MM-DD (machine-sortable/filterable);
   * formatted to long French for display via `formatRevisedDate`. */
  revisedAt: z.string(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type Word = z.infer<typeof wordSchema>;

export const wordSummarySchema = wordSchema
  .pick({
    id: true,
    numericId: true,
    slug: true,
    title: true,
    noyauPonderation: true,
    fonction: true,
    toCheck: true,
    reasons: true,
  })
  .extend({
    // Dominant noyau (highest weight, or `noyauSing` when all weights are zero),
    // used for the sidebar dot and coloured word-links. Null when neither exists.
    dominantNoyauCode: noyauCodeSchema.nullish(),
    dominantNoyauKey: noyauKeySchema.nullish(),
  });
export type WordSummary = z.infer<typeof wordSummarySchema>;
